import { useRef, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { BACK_DEPTH, useSessionBack } from '../../lib/sessionBack'
import { useTranslation } from 'react-i18next'
import { EMOTIONS, INTENSITY_LABELS } from '../../data/emotions'
import { localizeEmotion, localizeIntensityLabel } from '../../data/emotions.pl'
import type { SessionMode } from '../../types/session'

interface ArrivalProps {
  /**
   * Pre-selected emotion string from a prior session's "Explore this further" CTA.
   * May be a comma-separated multi-select (e.g. "shame, grief"). Split and validated
   * against EMOTIONS on component init; only valid emotion words are pre-selected.
   */
  preselectedEmotion?: string
  /**
   * Updated signature: now passes the chosen session lens (sessionMode) so
   * SessionPage can branch downstream phases (Feel → RAIN, See → The Work, etc.).
   */
  onComplete: (emotion: string, intensity: number, sessionMode: SessionMode) => void
}

/**
 * Two-step Arrival flow:
 *   Step 1 — Emotion picker + intensity slider.
 *   Step 2 — Lens picker — user chooses Standard, The Work, or RAIN.
 *
 * The lens (sessionMode) is selected per session and threads through to the
 * Edge Function's buildSystemPrompt to switch facilitation styles in phases
 * 2/3/4/6. Selecting a lens immediately calls onComplete — no extra confirm.
 *
 * "Choose for me" uses a rule-based emotion → lens mapping (no API call).
 * Inquiry wins for belief/judgment emotions; RAIN wins for somatic/overwhelm
 * states at intensity ≥ 7; Standard is the default for everything else.
 */
export default function Arrival({ preselectedEmotion, onComplete }: ArrivalProps) {
  const { t, i18n } = useTranslation()

  /*
   * Display copy only. Emotion.word stays the English identifier everywhere it
   * is toggled, compared or persisted — see the note in emotions.pl.ts. Only
   * `label`, `hint` and the intensity wording change with language.
   */
  const emotions = EMOTIONS.map(e => localizeEmotion(e, i18n.language))
  const intensityLabel = (n: number) => localizeIntensityLabel(n, INTENSITY_LABELS, i18n.language)
  /** Display label for a stored emotion id, falling back to the id itself. */
  const labelFor = (word: string) => emotions.find(e => e.word === word)?.label ?? word

  const [step, setStep] = useState<1 | 2>(1)
  /**
   * Initialize selected emotions from preselectedEmotion if provided.
   * Split on ', ' to handle multi-select strings, then filter to only words
   * that exist in EMOTIONS so stale or invalid values don't show as selected.
   * useState initializer runs once on mount — the value is stable.
   */
  const [selected, setSelected] = useState<string[]>(() => {
    if (!preselectedEmotion) return []
    const words = preselectedEmotion.split(', ').map(w => w.trim()).filter(Boolean)
    return words.filter(w => EMOTIONS.some(e => e.word === w))
  })
  const [intensity, setIntensity] = useState(5)
  const [hoveredHint, setHoveredHint] = useState<string | null>(null)
  // Lens suggestion state — null until user clicks "Choose for me"
  const [suggestion, setSuggestion] = useState<{ mode: SessionMode; reason: string } | null>(null)
  // Pre-session warning — shown when high intensity or heavy emotion selected
  const [showWarning, setShowWarning] = useState(false)
  // The warning sits between the two steps and has its own number.
  useScreenId(showWarning ? 'arrival.warning' : step === 2 ? 'arrival.lens' : 'arrival.emotion')

  // Whether the warning was shown on the way to the lens picker. The warning is
  // only sometimes part of the path, so Back from the lens picker has to know
  // which screen the person actually came from.
  const cameViaWarning = useRef(false)

  // Back: lens picker -> (warning ->) emotion picker. Arrival's screens are not
  // all states of one variable (the warning is a flag on top of step 1), so it
  // answers Back itself. From the emotion picker there is nothing earlier here.
  useSessionBack(() => {
    if (showWarning) {
      setShowWarning(false)
      return 'handled'
    }
    if (step === 2) {
      setStep(1)
      if (cameViaWarning.current) setShowWarning(true)
      return 'handled'
    }
    return 'first'
  }, BACK_DEPTH.step)

  function toggle(word: string) {
    setSelected(prev =>
      prev.includes(word)
        ? prev.filter(w => w !== word)
        : [...prev, word]
    )
  }

  function handleAdvanceToLens() {
    if (selected.length === 0) return
    const heavyEmotions = ['shame', 'grief', 'worthlessness', 'despair', 'helplessness', 'terror', 'dread']
    const lower = selected.map(e => e.toLowerCase())
    const isHeavy = intensity >= 8 || lower.some(e => heavyEmotions.includes(e))
    cameViaWarning.current = isHeavy
    if (isHeavy) {
      setShowWarning(true)
      return
    }
    setStep(2)
  }

  function handleLensSelect(mode: SessionMode) {
    // Cap at 500 chars to match server-side limit — guards against extreme multi-select
    const emotionString = selected.join(', ').slice(0, 500)
    onComplete(emotionString, intensity, mode)
  }

  /**
   * Maps selected emotions + intensity to the most fitting lens.
   * Inquiry: belief/judgment emotions (anger, resentment, guilt, shame…)
   * RAIN:    somatic/overwhelm emotions (anxiety, grief, fear, numbness…), especially at high intensity
   * Standard: everything else — the default safe container
   */
  function suggestLens(): { mode: SessionMode; reason: string } {
    const lower = selected.map(e => e.toLowerCase())
    const workTriggers = ['anger', 'resentment', 'betrayal', 'bitterness', 'guilt', 'shame', 'self-criticism', 'jealousy', 'envy', 'rejection', 'disgust', 'frustration']
    const rainTriggers = ['grief', 'sadness', 'fear', 'overwhelm', 'anxiety', 'heartache', 'despair', 'helplessness', 'numbness', 'tension', 'emptiness', 'loneliness', 'disconnection']
    const hasWork = lower.some(e => workTriggers.includes(e))
    const hasRain = lower.some(e => rainTriggers.includes(e))
    if (hasWork && !hasRain) return { mode: 'the_work', reason: t('arrival.reasonWork') }
    if (hasRain && (intensity >= 7 || !hasWork)) return { mode: 'rain', reason: t('arrival.reasonRain') }
    return { mode: 'standard', reason: t('arrival.reasonStandard') }
  }

  const lensLabel = (mode: SessionMode) =>
    mode === 'standard' ? t('arrival.modeStandard')
      : mode === 'the_work' ? t('arrival.modeInquiry')
        : t('arrival.modeRain')

  const activeHint =
    hoveredHint ?? (selected.length === 1 ? emotions.find(e => e.word === selected[0])?.hint : null)

  // ─── Pre-session warning — heavy emotion or intensity ≥ 8 ─────────────────
  if (showWarning) {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
          <div className="mb-8">
            <p className="text-xs font-medium text-[#9B72C0] uppercase tracking-widest mb-3">
              {t('arrival.warningLabel')}
            </p>
            <h2 className="font-display text-[1.5rem] text-[#1C1128] font-normal leading-snug mb-4">
              {t('arrival.warningHeading')}
            </h2>
            <div
              className="px-5 py-4 rounded-xl"
              style={{
                background: 'rgba(61,31,98,0.04)',
                border: '1.5px solid #D4C0E8',
              }}
            >
              <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>
                {t('arrival.warningBody')}
              </p>
            </div>
          </div>

          <div className="flex-1" />

          <button
            onClick={() => { setShowWarning(false); setStep(2) }}
            className="w-full py-4 rounded-xl bg-[#3D1F62] text-white font-medium tracking-wide hover:bg-[#4A2875] active:scale-[0.98] transition-all"
          >
            {t('session.continue')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Step 2: Lens picker ───────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          {/* Header */}
          <div className="mb-6">
            <p className="text-xs font-medium text-[#9B72C0] uppercase tracking-widest mb-3">
              {t('arrival.chooseApproach')}
            </p>
            <h2 className="font-display text-[1.5rem] text-[#1C1128] font-normal leading-snug">
              {t('arrival.howWorkToday')}
            </h2>
            <p className="text-[#6B5F7A] text-sm mt-2">
              {/* Split rather than one interpolated string so the emphasis on the
                  button's own name survives translation. */}
              {t('arrival.lensExplainer')} {t('arrival.lensNotSure')} <em>{t('arrival.chooseForMe')}</em> {t('arrival.useBelow')}
            </p>
          </div>

          {/* Suggestion banner — shown after "Choose for me" */}
          {suggestion && (
            <div className="mb-4 rounded-xl px-4 py-3 page-enter" style={{
              background: 'rgba(61,31,98,0.05)',
              border: '1.5px solid #D4C0E8',
            }}>
              <p className="text-xs leading-relaxed" style={{ color: '#3D1F62' }}>
                <span className="font-medium">{t('arrival.suggestedPrefix')} {lensLabel(suggestion.mode)}</span> — {suggestion.reason}
              </p>
            </div>
          )}

          {/* ── Standard lens ─────────────────────────────────────────────── */}
          <button
            onClick={() => handleLensSelect('standard')}
            className="px-5 py-4 rounded-2xl cursor-pointer mb-3 text-left transition-all w-full"
            style={{
              background: suggestion?.mode === 'standard' ? 'rgba(155,114,192,0.05)' : 'white',
              borderTop: `3px solid ${suggestion?.mode === 'standard' ? '#9B72C0' : '#D8CCE8'}`,
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#9B72C0' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = suggestion?.mode === 'standard' ? '#9B72C0' : '#D8CCE8' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium" style={{ color: '#1C1128' }}>{t('arrival.modeStandard')}</p>
              {suggestion?.mode === 'standard' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(155,114,192,0.15)', color: '#6B3FA0' }}>{t('arrival.suggested')}</span>
              )}
            </div>
            <p className="text-xs leading-relaxed mt-1" style={{ color: '#6B5F7A' }}>
              {t('arrival.modeStandardDesc')}
            </p>
          </button>

          {/* ── Inquiry lens ──────────────────────────────────────────────── */}
          <button
            onClick={() => handleLensSelect('the_work')}
            className="px-5 py-4 rounded-2xl cursor-pointer mb-3 text-left transition-all w-full"
            style={{
              background: suggestion?.mode === 'the_work' ? 'rgba(196,168,48,0.04)' : 'white',
              borderTop: `3px solid ${suggestion?.mode === 'the_work' ? '#C4A830' : '#E8E0C0'}`,
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#C4A830' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = suggestion?.mode === 'the_work' ? '#C4A830' : '#E8E0C0' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium" style={{ color: '#1C1128' }}>{t('arrival.modeInquiry')}</p>
              {suggestion?.mode === 'the_work' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(196,168,48,0.15)', color: '#8A6800' }}>{t('arrival.suggested')}</span>
              )}
            </div>
            <p className="text-xs leading-relaxed mt-1" style={{ color: '#6B5F7A' }}>
              {t('arrival.modeInquiryDesc')}
            </p>
          </button>

          {/* ── RAIN ──────────────────────────────────────────────────────── */}
          <button
            onClick={() => handleLensSelect('rain')}
            className="px-5 py-4 rounded-2xl cursor-pointer mb-3 text-left transition-all w-full"
            style={{
              background: suggestion?.mode === 'rain' ? 'rgba(58,155,122,0.04)' : 'white',
              borderTop: `3px solid ${suggestion?.mode === 'rain' ? '#3A9B7A' : '#C0E4DA'}`,
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#3A9B7A' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = suggestion?.mode === 'rain' ? '#3A9B7A' : '#C0E4DA' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium" style={{ color: '#1C1128' }}>{t('arrival.modeRain')}</p>
              {suggestion?.mode === 'rain' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(58,155,122,0.15)', color: '#2A7A5A' }}>{t('arrival.suggested')}</span>
              )}
            </div>
            <p className="text-xs leading-relaxed mt-1" style={{ color: '#6B5F7A' }}>
              {t('arrival.modeRainDesc')}
            </p>
          </button>

          <div className="flex-1" />

          {/* Choose for me — shown until a suggestion exists */}
          {!suggestion ? (
            <button
              onClick={() => setSuggestion(suggestLens())}
              className="w-full py-3 rounded-xl text-sm font-medium mb-3"
              style={{
                background: 'rgba(61,31,98,0.06)',
                border: '1.5px solid #D4C0E8',
                color: '#3D1F62',
                cursor: 'pointer',
              }}
            >
              {t('arrival.chooseForMe')}
            </button>
          ) : (
            /* After suggestion: primary CTA uses the suggested mode */
            <button
              onClick={() => handleLensSelect(suggestion.mode)}
              className="w-full py-3 rounded-xl text-sm font-medium mb-3 page-enter"
              style={{
                background: '#3D1F62',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              {t('arrival.use', { mode: lensLabel(suggestion.mode) })}
            </button>
          )}

          {/* Skip link — always defaults to Standard */}
          <button
            onClick={() => handleLensSelect('standard')}
            className="w-full py-2 text-sm"
            style={{ background: 'transparent', border: 'none', color: '#9B8FAA', cursor: 'pointer' }}
          >
            {t('arrival.skipUseStandard')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Step 1: Emotion picker + intensity ────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {/* Header */}
        <div className="mb-10">
          <p className="text-xs font-medium text-[#9B72C0] uppercase tracking-widest mb-3">
            {t('session.phases.arrival')}
          </p>
          <h2 className="font-display text-[1.75rem] text-[#1C1128] font-normal leading-snug">
            {t('arrival.heading')}
          </h2>
          <p className="text-[#6B5F7A] text-sm mt-2">
            {t('arrival.chooseWords')}
          </p>
          <p className="text-xs mt-3" style={{ color: '#9B8FAA' }}>
            {t('arrival.noRightAnswer')}
          </p>
        </div>

        {/* Emotion grid */}
        <div className="flex flex-wrap gap-2.5 mb-8">
          {emotions.map(({ word, hint, label }) => (
            <button
              key={word}
              className={`emotion-pill ${selected.includes(word) ? 'selected' : ''}`}
              onClick={() => toggle(word)}
              onMouseEnter={() => setHoveredHint(hint)}
              onMouseLeave={() => setHoveredHint(null)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Hint tooltip */}
        <div className="min-h-[1.4rem] mb-8">
          {activeHint && (
            <p className="text-xs text-[#6B5F7A] italic transition-all">
              {activeHint}
            </p>
          )}
        </div>

        {/* Intensity slider — shown once at least one emotion is selected */}
        {selected.length > 0 && (
          <div className="mb-10 page-enter">
            <div className="flex items-baseline justify-between mb-3">
              <label className="text-sm text-[#1C1128] font-medium">
                {t('arrival.intensityLabel')}
              </label>
              <div className="text-right">
                <span className="text-2xl font-display text-[#3D1F62]">{intensity}</span>
                <span className="text-xs text-[#6B5F7A] ml-1">/ 10</span>
              </div>
            </div>

            <input
              type="range"
              min={1}
              max={10}
              value={intensity}
              onChange={e => setIntensity(Number(e.target.value))}
              className="w-full mb-2"
            />

            <div className="flex justify-between text-xs text-[#9B72C0]">
              <span>{intensityLabel(1)}</span>
              <span className="italic text-[#6B5F7A]">{intensityLabel(intensity)}</span>
              <span>{intensityLabel(10)}</span>
            </div>
          </div>
        )}

        <div className="flex-1" />

        <div className="pb-6">
          {selected.length > 0 && (
            <p className="text-center text-xs text-[#6B5F7A] mb-4 italic page-enter">
              {/*
                `selected` holds English identifiers, so each one is mapped to
                its display label before rendering. Falls back to the id itself
                if an emotion has no entry, which keeps the sentence intact.
              */}
              {selected.length === 1
                ? <>{t('arrival.youreFeeling')} <strong className="text-[#3D1F62] not-italic">{labelFor(selected[0])}</strong> — {intensityLabel(intensity)}.</>
                : <>{t('arrival.youreFeeling')} <strong className="text-[#3D1F62] not-italic">{selected.slice(0, -1).map(labelFor).join(', ')}</strong> {t('common.and')} <strong className="text-[#3D1F62] not-italic">{labelFor(selected[selected.length - 1])}</strong> — {intensityLabel(intensity)}.</>
              }
            </p>
          )}
          <button
            onClick={handleAdvanceToLens}
            disabled={selected.length === 0}
            className="w-full py-4 rounded-xl bg-[#3D1F62] text-white font-medium tracking-wide hover:bg-[#4A2875] active:scale-[0.98] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {t('arrival.beginSession')}
          </button>
        </div>
      </div>
    </div>
  )
}
